import { useParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate, faArrowUpRightFromSquare, faEllipsisH } from '@fortawesome/free-solid-svg-icons';
import { NavLink } from 'react-router-dom';

import EntityTable from "../Listing/EntityTable";
import Accordion from "../NavTab/Accordion";
import InputText from "../Form/Fields/InputText";
import { API_BASE_URL } from '../../api';

const formatAmount = (val) =>
  new Intl.NumberFormat('fr-MA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val ?? 0);

const StatusBadge = () => {
  const { t } = useTranslation();
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      background: '#ecfdf5', color: '#065f46',
      padding: '4px 10px', borderRadius: 999,
      fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981', flexShrink: 0 }} />
      {t('returns.status.processed')}
    </span>
  );
};

const PatientReturns = () => {
  const { id } = useParams();
  const { t } = useTranslation();

  const columns = [
    {
      Header: t('returns.column.reference'),
      accessor: 'reference',
      sortable: true,
      Cell: (row) => <NavLink to={`/returns/${row.id}`} className="cell-name">{row.reference}</NavLink>,
    },
    {
      Header: t('Order'),
      accessor: 'orderReference',
      sortable: true,
      className: 'text-center',
      filterable: { component: InputText },
      Cell: (row) => (
        <NavLink
          to={`/order/${row.orderId}`}
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 28, height: 28, borderRadius: 8,
            background: '#f3f4f6', color: '#6b7280',
          }}
          onClick={(e) => e.stopPropagation()}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#2563eb'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = '#f3f4f6'; e.currentTarget.style.color = '#6b7280'; }}
          data-tooltip-id="tooltip"
          data-tooltip-content={row.orderReference}
        >
          <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ fontSize: 12 }} />
        </NavLink>
      ),
    },
    {
      Header: t('returns.column.items'),
      accessor: 'itemCount',
      className: 'text-center',
      Cell: (row) => (
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 28, height: 28, borderRadius: '50%',
          background: '#f3f4f6', fontSize: 13, fontWeight: 700, color: '#374151',
        }}>
          {row.itemCount}
        </span>
      ),
    },
    {
      Header: t('returns.column.amount'),
      accessor: 'total',
      sortable: true,
      Cell: (row) => (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>{formatAmount(row.total)}</span>
          <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 500 }}>dhs</span>
        </div>
      ),
    },
    {
      Header: t('returns.column.reason'),
      accessor: 'reason',
      Cell: (row) => row.reason
        ? <span style={{ fontSize: 13, color: '#6b7280', maxWidth: 200, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.reason}</span>
        : <span className="cell-muted">—</span>,
    },
    {
      Header: t('Status'),
      accessor: 'status',
      className: 'text-center',
      Cell: () => <StatusBadge />,
    },
    {
      Header: '',
      accessor: 'actions',
      className: 'col-actions',
      Cell: (row) => (
        <div className="cell-dropdown">
          <button className="cell-actions-btn dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
            <FontAwesomeIcon icon={faEllipsisH} />
          </button>
          <ul className="dropdown-menu dropdown-menu-end">
            <li>
              <NavLink to={`/returns/${row.id}`} className="dropdown-item">
                {t('returns.action.view_return')}
              </NavLink>
            </li>
            <li>
              <NavLink to={`/order/${row.orderId}`} className="dropdown-item">
                {t('View order')}
              </NavLink>
            </li>
          </ul>
        </div>
      ),
    },
  ];

  // Card-view design for this listing (Table's enableCardView feature, forced on mobile)
  // — reuses the actions column's own dropdown so both views offer identical actions.
  const returnsActionsColumn = columns.find((c) => c.accessor === 'actions');
  const renderReturnCard = (row) => (
    <div className="card h-100 shadow-sm">
      <div className="card-body d-flex flex-column" style={{ padding: 14, gap: 6 }}>
        <div style={{ alignSelf: 'flex-start' }}><StatusBadge /></div>

        <NavLink to={`/returns/${row.id}`} style={{ fontSize: 13.5, fontWeight: 600, color: '#111827', marginTop: 4 }}>
          {row.reference}
        </NavLink>

        {row.reason && (
          <span style={{ fontSize: 11.5, color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {row.reason}
          </span>
        )}

        <div className="d-flex align-items-center justify-content-between" style={{ marginTop: 'auto', paddingTop: 6 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{formatAmount(row.total)}</span>
            <span style={{ fontSize: 10.5, color: '#9ca3af', fontWeight: 500 }}>dhs</span>
            <span style={{
              marginLeft: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 22, height: 22, borderRadius: '50%',
              background: '#f3f4f6', fontSize: 11, fontWeight: 700, color: '#374151',
            }}>
              {row.itemCount}
            </span>
          </div>
          {returnsActionsColumn?.Cell(row)}
        </div>
      </div>
    </div>
  );

  return (
    <Accordion
      id="patientReturns"
      parent="patient_information_container"
      title={t('Returns')}
      icon={faArrowsRotate}
      flush
    >
      <EntityTable
        endpoint={`${API_BASE_URL}/api/v1/returns`}
        columns={columns}
        extraParams={{
          patientId: id
        }}
        defaultSort="id"
        defaultDirection="desc"
        pageSize={20}
        emptyMessage={t('returns.empty')}
        searchable={true}
        multiSelect={false}
        enableCardView={true}
        renderCard={renderReturnCard}
      />
    </Accordion>
  );
};

export default PatientReturns;
